import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { withRouter } from 'utils/withRouter';
import { injectIntl, FormattedMessage, FormattedNumber } from 'react-intl';
import { Button, paddingProps, fontWeightProps, FlexCenter, Flex } from 'ui';
import { withEllipsis } from 'ui/styles';
import { decodeUrlParams } from 'utils/helpers';
import { isToggleEnabled } from 'react-awesome-feature-toggles';
import SearchItem from './search-item';
import { getObjectTypeById } from '../../utils/helpers';
import { referenceTypes } from '../../utils/app-configs';
import useWindowSize from '../../utils/hooks/useWindowSize';

const ResultsBlock = styled(Flex)`
  margin-right: ${p => p.functionalLocationsEnabled ? '20px' : '3rem'};
  margin-bottom: 20px;
  ${p => p.fullPage ? 'min-width: 350px' : 'width: 350px'};
  ${p => p.fillWidth && `
    flex: 1;
    max-width: ${p.maxWidth}px;
  `}
  ${withEllipsis};
`;

const Header = styled.div`
  border-bottom: 1px solid ${p => p.theme.colors.border};
  padding-bottom: 14px;
  margin-bottom: 5px;
`;

const HeaderItem = styled.span`
  ${paddingProps};
  ${fontWeightProps};
  border-bottom: 3px solid ${p => p.theme.colors.theme};
  padding-bottom: 10px;
  font-size: ${p => p.theme.fontSize.large};
  font-weight: ${p => p.theme.fontWeight.bolder};
  color: ${p => p.theme.colors.text};
`;

const ShowAllButton = styled(Button)`
  height: 40px;
  padding: 0 10px;
  border: 2px solid ${p => p.theme.colors.theme};
  background-color: transparent;
  white-space: pre-wrap;
  font-weight: ${p => p.theme.fontWeight.bold};
  text-transform: uppercase;
  color: ${p => p.theme.colors.text};
  &:hover {
    background-color: white;
    border-color: ${p => p.theme.colors.darkTheme};
    color: ${p => p.theme.colors.textDark};
  }
`;

const FullPage = styled.div`
  ${p =>
    p.active &&
    css`
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: flex-start;
    `};
`;

const SearchResultBlock = ({
  name,
  totalCount = 0,
  items,
  type,
  fullPage = false,
  showAllButton = true,
  intl,
  history,
  match,
  isEquipment = false,
  blockRedirect = false,
}) => {
  const [width] = useWindowSize();
  const [maxWidth, setMaxWidth] = useState();
  const showAllItems = () => {
    // navigate to show all items route;
    const { searchValue, installedAtId, relationId } = match.params;
    if (installedAtId) {
      history.push(
        // eslint-disable-next-line
        `/search/${searchValue}/type/${type}/installedAt/${installedAtId}${relationId ? `/relations/${relationId}` : ''}`,
      );
    } else {
      history.push(
        `/search/${searchValue}/type/${type}${relationId ? `/relations/${relationId}` : ''}`,
      );
    }
  };

  const getMaxWidth = () => {
    if (width) {
      const equipmentWidth = width - 840;
      if (items?.length * 375 > equipmentWidth) {
        setMaxWidth(375 * Math.floor(equipmentWidth / 375));
      } else {
        setMaxWidth(items?.length * 375 || 375);
      }
    }
  };

  useEffect(() => {
    if (width && isEquipment) {
      getMaxWidth();
    }
  }, [width, items?.length]);

  const renderNoMatches = () => {
    if (!items || items.length === 0 || totalCount === 0) {
      return <>{intl.formatMessage({ id: 'search.NO_MATCH' })}</>;
    }
    return null;
  };

  const renderShowAllButton = () => {
    if (items && items.length > 0 && totalCount > 0 && items.length < totalCount) {
      return (
        <FlexCenter paddingTop={10}>
          <ShowAllButton onClick={showAllItems}>
            {intl.formatMessage(
              { id: 'search.SHOW_ALL' },
              { count: intl.formatNumber(totalCount) },
            )}
          </ShowAllButton>
        </FlexCenter>
      );
    }
    return null;
  };

  const shouldRenderItems = items && items.length > 0 && totalCount > 0;
  const params = decodeUrlParams(match.params);
  const objectType = getObjectTypeById(params.relationId);

  return (
    <ResultsBlock
      data-test-id={name}
      fullPage={fullPage}
      fdc
      fillWidth={
        isEquipment &&
        (
          objectType === referenceTypes.installedAt ||
          objectType === referenceTypes.customer
        )
      }
      maxWidth={maxWidth || 375}
      functionalLocationsEnabled={isToggleEnabled('functionalLocationSearch')}
    >
      <Header>
        <HeaderItem fontWeightBold>
          <FormattedMessage id={name} />
        </HeaderItem>
        <HeaderItem paddingLeft={5}>
          ({<FormattedNumber value={totalCount} />})
        </HeaderItem>
      </Header>
      <>
        <FullPage
          active={fullPage}
        >
          {shouldRenderItems &&
            items?.map(item =>
              <SearchItem
                fullPage={fullPage}
                key={item.uri}
                item={item}
                history={history}
                active={params.relationId === item.id}
                hasRelations={item.hasRelations}
                blockRedirect={blockRedirect}
              />,
            )}
        </FullPage>
        {renderNoMatches()}
      </>
      {showAllButton && renderShowAllButton()}
    </ResultsBlock>
  );
};

SearchResultBlock.propTypes = {
  name: PropTypes.string.isRequired,
  totalCount: PropTypes.number,
  items: PropTypes.array,
  type: PropTypes.string,
  fullPage: PropTypes.bool,
  showAllButton: PropTypes.bool,
  intl: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  isEquipment: PropTypes.bool,
  blockRedirect: PropTypes.bool,
};

export default withRouter(injectIntl(SearchResultBlock));
